import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookie from 'react-cookie';
import { FormattedMessage } from 'react-intl';
import { get, isEmpty, split } from 'lodash';
import { updateProgress } from '../app-main/actions/flow';
import Contracts from '../../../api/api-contracts';
import { useLoading } from '../shared-components/utils/hooks/use-loading';
import { usePrevious } from '../shared-components/utils/hooks/use-previous';

// Hook for handling breadcrumbs component behavior for inquiry flow
export const useInquiryBreadcrumbs = (listingId, flow, dispatch) => {
  const { step = 0 } = flow;

  const steps = useMemo(() => {
    return [
      {
        step: 1,
        text: <FormattedMessage
          id="inquiry_booking.inquiry_breadcrumbs.listing_details"
          defaultMessage="Listing Details"
        />,
        link: `/pages/listings/${listingId}`
      },
      {
        step: 2,
        text: <FormattedMessage
          id="inquiry_booking.inquiry_breadcrumbs.message_host"
          defaultMessage="Message Host"
        />
      }
    ];
  }, []);

  const onClick = useCallback((step) => {
    dispatch(updateProgress(step));
  }, [step]);

  return { currentStep: step, steps, onClick };
};

const createInquiryPayload = (profile, listing, inputs) => {
  const {
    schedule,
    numberOfGuests,
    activity,
    alcoholConsumed,
    message,
    flexibleHoursSelected
  } = inputs;

  const { SSOid } = profile;
  const { ownerId } = listing;

  const payload = {
    guest_sso_id: SSOid,
    host_sso_id: ownerId,
    type: 'INQUIRY',
    items: [
      {
        type: 'BOOKING',
        info: {
          guest_id: profile?.SSOid,
          host_id: listing?.ownerId,
          listing_id: listing?._id,
          tags: { activity },
          schedule,
          number_of_guests: numberOfGuests,
          inquiry_message: message,
          ga_client_id: analyticsIntegrations?.getGoogleAnalyticsID(),
          attr_url: PSSessions.Cookies('PSAttrUrl'),
          attr_referrer: PSSessions.Cookies('PSAttrReferrer'),
          flexible_date_time: flexibleHoursSelected,
          granular_use_type: PSSessions.Cookies('PSSK'),
          alcohol_consumed: alcoholConsumed
        }
      }
    ]
  };

  return payload;
};

export const getInquiryQuote = (accessToken, profile, listing, inputs) => {
  const payload = createInquiryPayload(profile, listing, inputs);

  return new Promise((resolve, reject) => {
    Contracts.inquire(payload, true, accessToken, (res) => {
      if (res.error || 400 <= res.statusCode) {
        return reject(res);
      }

      return resolve(res);
    });
  });
};

export const sendInquiry = async (accessToken, profile, listing, inputs) => {
  const payload = createInquiryPayload(profile, listing, inputs);

  return new Promise((resolve, reject) => {
    Contracts.inquire(payload, false, accessToken, (res) => {
      if (res.error || 400 <= res.statusCode) {
        return reject(res);
      }

      return resolve(res);
    });
  });
};

// Hook that handles inquiry quote request with loading and error handling
export const useInquiry = (
  accessToken,
  profile,
  listing, {
    activity,
    numberOfGuests,
    alcoholConsumed,
    message,
    organization,
    schedule,
    profession
  },
  intl
) => {
  const [quote, setQuote] = useState({});
  const [errors, setErrors] = useState([]);

  const prevNumberOfGuests = usePrevious(numberOfGuests);

  const attendeePricingTiers = get(listing, 'attendee_pricing.tiers', []);
  const isAttendeesPricing = !isEmpty(attendeePricingTiers) && 1 < attendeePricingTiers.length;

  const isValid = () => {
    return get(schedule, '[0].start.time') && get(schedule, '[0].end.time');
  };

  const { asyncFn: getFeeCalc, isLoading: isQuoteLoading } = useLoading(async () => {
    if (!isValid()) {
      return;
    }
    try {
      setErrors([]);
      const feeCalcResponse = await getInquiryQuote(
        accessToken,
        profile,
        listing,
        {
          activity,
          numberOfGuests,
          alcoholConsumed,
          message,
          organization,
          schedule,
          profession
        }
      );
      setQuote(feeCalcResponse);
    } catch (e) {
      const apiErrorMessage = get(e, 'body.body.message',
        intl.formatMessage({
          id: 'inquiry_main.something_went_wrong',
          defaultMessage: 'Something went wrong, please try again'
        }));
      const errors = split(apiErrorMessage, '\n');
      setErrors(errors);
    }
  });

  const { asyncFn: sendInquiryRequest, isLoading: isInquiryLoading } = useLoading(async () => {
    if (!isValid()) {
      setErrors([intl.formatMessage({
        id: 'inquiry_booking.date_time_error',
        defaultMessage: 'Please add date and time'
      })]);
      return;
    }
    try {
      setErrors([]);
      const res = await sendInquiry(
        accessToken,
        profile,
        listing,
        {
          activity,
          numberOfGuests,
          alcoholConsumed,
          message,
          organization,
          schedule,
          profession
        }
      );
      setQuote(res);
      const cookieOptions = {
        expires: new Date(Date.now() + 3888000000), // 45 days
        path: '/'
      };
      Cookie.save('PSSK', activity, cookieOptions);
      Cookie.save('booking_schedule', schedule, cookieOptions);
      const attendeeTier = (listing?.attendee_pricing?.tiers || []).find(
        (tier) => tier.min === numberOfGuests
      );
      if (attendeeTier) {
        Cookie.save(
          'booking_attendee_tier',
          { [listing._id]: attendeeTier },
          cookieOptions
        );
      }
      window.location = `/pages/listings/${listing?._id}?inquiry=true`;
    } catch (e) {
      const apiErrorMessage = get(e, 'body.body.message',
        intl.formatMessage({
          id: 'inquiry_main.something_went_wrong',
          defaultMessage: 'Something went wrong, please try again'
        }));
      const errors = split(apiErrorMessage, '\n');
      setErrors(errors);
      analyticsIntegrations.formFail(
        'msg host form',
        'Missing or incorrect form inputs'
      );
    }
  });

  useEffect(() => {
    const isAttendeesChanged = prevNumberOfGuests !== numberOfGuests;

    const shouldFetch = isAttendeesChanged && isAttendeesPricing && isEmpty(errors);

    if (shouldFetch) {
      getFeeCalc();
    }
  }, [numberOfGuests]);

  useEffect(() => {
    if (!isEmpty(schedule)) {
      getFeeCalc();
    }
  }, []);

  return {
    getFeeCalc,
    isQuoteLoading,
    quote,
    sendInquiryRequest,
    isInquiryLoading,
    isAttendeesPricing,
    errors
  };
};

export const formatPrice = (value, currency, intl) => {
  return intl.formatNumber(value, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0 === value % 1 ? 0 : 2,
    maximumFractionDigits: 0 === value % 1 ? 0 : 2
  });
};
